import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  User,
  Building2,
  Zap,
  ShieldCheck,
  Headphones,
  BadgePercent,
  Clock,
  FileCheck,
  Lock,
} from "lucide-react";

export const Route = createFileRoute("/word-klant")({
  head: () => ({
    meta: [
      { title: "Word klant — Elvion Group Nederland helpt u graag verder" },
      { name: "description", content: "Word particuliere of zakelijke klant bij Elvion Group Nederland. Wij helpen u graag verder met offertes, bestellingen en persoonlijke service." },
      { property: "og:title", content: "Word klant — Elvion Group Nederland helpt u graag verder" },
      { property: "og:description", content: "Word particuliere of zakelijke klant bij Elvion Group Nederland. Wij helpen u graag verder met offertes, bestellingen en persoonlijke service." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://elvion-group.nl/word-klant" },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/word-klant" }],
  }),
  component: WordKlantPage,
});

function WordKlantPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-hero-gradient border-b border-border">
          <div className="container mx-auto px-4 py-16 md:py-24">
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm font-medium text-white/80 backdrop-blur-sm">
                <Zap className="h-4 w-4" /> Gratis account
              </span>
              <h1 className="mt-6 text-4xl font-bold tracking-tight text-white md:text-6xl">
                Word klant bij Elvion Group Nederland
              </h1>
              <p className="mt-4 text-lg text-white/70 md:text-xl">
                Of je nu particulier bent of zakelijk onderweg — bij ons ben je aan het juiste adres. 
                Kies het account dat bij jou past en profiteer direct van persoonlijke service.
              </p>
            </div>
          </div>
        </section>

        {/* Account types */}
        <section className="container mx-auto px-4 py-16 md:py-20">
          <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-2">
            {/* Particulier */}
            <div className="group relative rounded-2xl border border-border bg-card p-8 shadow-sm transition-all hover:shadow-md hover:border-primary/30">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <User className="h-7 w-7" />
              </div>
              <h2 className="mt-6 text-2xl font-bold text-foreground">
                Particulier
              </h2>
              <p className="mt-2 text-muted-foreground">
                Voor huiseigenaren, verenigingen en kleine projecten. Direct toegang tot webshop-prijzen en persoonlijke ondersteuning.
              </p>
              <ul className="mt-6 space-y-3">
                <li className="flex items-start gap-3 text-sm text-foreground">
                  <Zap className="mt-0.5 h-4 w-4 shrink-0 text-grass" />
                  <span>Inloggen met Google of e-mail</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-foreground">
                  <BadgePercent className="mt-0.5 h-4 w-4 shrink-0 text-grass" />
                  <span>Toegang tot webshop-prijzen</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-foreground">
                  <Headphones className="mt-0.5 h-4 w-4 shrink-0 text-grass" />
                  <span>Persoonlijk advies en support</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-foreground">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-grass" />
                  <span>Offertes en bestellingen inzien</span>
                </li>
              </ul>
              <div className="mt-8">
                <Link to="/auth">
                  <Button className="w-full group/btn">
                    Account aanmaken
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" />
                  </Button>
                </Link>
                <p className="mt-3 text-center text-xs text-muted-foreground">
                  Al een account?{" "}
                  <Link to="/auth" className="text-primary hover:underline">
                    Inloggen
                  </Link>
                </p>
              </div>
            </div>

            {/* Zakelijk */}
            <div className="group relative rounded-2xl border border-border bg-card p-8 shadow-sm transition-all hover:shadow-md hover:border-primary/30">
              <div className="absolute right-6 top-6 rounded-full bg-orange/10 px-3 py-1 text-xs font-semibold text-orange">
                Inkoopprijzen
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Building2 className="h-7 w-7" />
              </div>
              <h2 className="mt-6 text-2xl font-bold text-foreground">
                Zakelijk
              </h2>
              <p className="mt-2 text-muted-foreground">
                Voor aannemers, installateurs en bedrijven. Na KvK-verificatie toegang tot inkoopprijzen en zakelijke voorwaarden.
              </p>
              <ul className="mt-6 space-y-3">
                <li className="flex items-start gap-3 text-sm text-foreground">
                  <BadgePercent className="mt-0.5 h-4 w-4 shrink-0 text-grass" />
                  <span>Toegang tot inkoopprijzen</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-foreground">
                  <FileCheck className="mt-0.5 h-4 w-4 shrink-0 text-grass" />
                  <span>Offertes digitaal ondertekenen</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-foreground">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-grass" />
                  <span>Eigen accountmanager</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-foreground">
                  <Lock className="mt-0.5 h-4 w-4 shrink-0 text-grass" />
                  <span>2FA-beveiligd klantportaal</span>
                </li>
              </ul>
              <div className="mt-8">
                <Link to="/auth">
                  <Button variant="outline" className="w-full group/btn border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                    Zakelijke account aanvragen
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" />
                  </Button>
                </Link>
                <p className="mt-3 text-center text-xs text-muted-foreground">
                  Goedkeuring binnen 1 werkdag na KvK-upload
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section className="border-t border-border bg-muted/30">
          <div className="container mx-auto px-4 py-12">
            <div className="mx-auto grid max-w-4xl grid-cols-2 gap-8 text-center md:grid-cols-4">
              <div>
                <div className="text-3xl font-bold text-foreground">500+</div>
                <div className="mt-1 text-sm text-muted-foreground">Tevreden klanten</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-foreground">15+</div>
                <div className="mt-1 text-sm text-muted-foreground">Jaar ervaring</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-foreground">24/7</div>
                <div className="mt-1 text-sm text-muted-foreground">Storingsdienst</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-foreground">100%</div>
                <div className="mt-1 text-sm text-muted-foreground">NEN-gecertificeerd</div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ teaser */}
        <section className="container mx-auto px-4 py-16 md:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-foreground">Veelgestelde vragen</h2>
            <div className="mt-8 space-y-4 text-left">
              <details className="group rounded-xl border border-border bg-card p-5 open:bg-muted/20">
                <summary className="flex cursor-pointer list-none items-center justify-between font-medium text-foreground">
                  Wat kost een klantaccount?
                  <span className="ml-4 transition group-open:rotate-180">▼</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">
                  Een klantaccount is volledig gratis. Je betaalt alleen voor de producten en diensten die je afneemt.
                </p>
              </details>
              <details className="group rounded-xl border border-border bg-card p-5 open:bg-muted/20">
                <summary className="flex cursor-pointer list-none items-center justify-between font-medium text-foreground">
                  Hoe snel kan ik als zakelijke klant aan de slag?
                  <span className="ml-4 transition group-open:rotate-180">▼</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">
                  Na het uploaden van je KvK-uittreksel controleren wij je aanvraag doorgaans binnen één werkdag. Daarna activeer je account direct.
                </p>
              </details>
              <details className="group rounded-xl border border-border bg-card p-5 open:bg-muted/20">
                <summary className="flex cursor-pointer list-none items-center justify-between font-medium text-foreground">
                  Kan ik later van particulier naar zakelijk overstappen?
                  <span className="ml-4 transition group-open:rotate-180">▼</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">
                  Ja, dat is geen probleem. Neem contact op met onze klantenservice dan helpen wij je graag bij het upgraden van je account.
                </p>
              </details>
            </div>
            <div className="mt-8">
              <Link
                to="/klantenservice"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
              >
                Alle vragen bekijken <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
